// Pure label-merge helper functions for the place-selection Review flow.
//
// These are intentionally free of any Vue/component or Vuex dependency so that
// they can be imported and exercised directly. They mirror the extraction
// pattern used by placeSelectionSaveHelpers.js (specifically `applyMarkSaved`)
// in this same folder.
//
// The store mutation (`addLabelsToLibraryPlaces`) delegates to these helpers,
// so the same code paths that production uses stay isolated and testable.
// Feature: library-bulk-tagging (Task 4)

/**
 * Merge a list of labels onto a single place, immutably.
 *
 * Returns a NEW place object whose `labels` is the existing labels followed by
 * any of `labels` not already present, compared case-sensitively (exact string
 * compare). Each distinct label appears exactly once, existing order preserved
 * first, then newly appended order. The input `place` is never mutated (its
 * `labels` array is copied before appending).
 *
 * @param {Object} place - The place to merge labels onto.
 * @param {Array<string>} labels - The labels to append.
 * @returns {Object} A new place object with merged labels.
 */
export function mergeLabels(place, labels) {
  const existing = Array.isArray(place && place.labels) ? place.labels : [];
  const toAdd = Array.isArray(labels) ? labels : [];

  const merged = existing.slice();
  const seen = new Set(merged);

  for (const label of toAdd) {
    if (!seen.has(label)) {
      seen.add(label);
      merged.push(label);
    }
  }

  return { ...place, labels: merged };
}

/**
 * Immutable label-merge reducer over an array of selected places.
 *
 * Returns a NEW array in which every targeted place is replaced by a new object
 * (via {@link mergeLabels}) and every other place is passed through by its
 * original reference. Originals are never mutated.
 *
 * Targeting:
 * - Empty / non-array `labels` -> no-op: the input array is returned unchanged
 *   (same reference), and no place is touched.
 * - No `ids` (missing or empty) -> target every place with
 *   `_source === 'library'`.
 * - Non-empty `ids` -> target only places whose `_id` is contained in `ids`;
 *   ids that match no place are ignored.
 *
 * @param {Array<Object>} places - The selected places.
 * @param {{ ids?: Array<string>, labels?: Array<string> }} [payload]
 * @returns {Array<Object>} A new array with matched places' labels merged, or
 *   the original array when the operation is a no-op.
 */
export function applyLabelMerge(places, { ids, labels } = {}) {
  const source = Array.isArray(places) ? places : [];

  // No-op when there is nothing to merge: return the input unchanged.
  if (!Array.isArray(labels) || labels.length === 0) {
    return places;
  }

  const idList = Array.isArray(ids) ? ids : [];
  const hasIds = idList.length > 0;
  const idSet = hasIds ? new Set(idList) : null;

  const isTargeted = (place) => {
    if (!place) {
      return false;
    }
    if (hasIds) {
      return idSet.has(place._id);
    }
    return place._source === "library";
  };

  return source.map((place) =>
    isTargeted(place) ? mergeLabels(place, labels) : place
  );
}
